import { useMemo, useImperativeHandle, forwardRef } from "react";
import { Typography, Box } from "@mui/material";
import * as XLSX from "xlsx";

const StaffDistributionTable = forwardRef(
  ({ data, sortBy = "district" }, ref) => {
    useImperativeHandle(ref, () => ({
      exportToExcel,
    }));

    const processedData = useMemo(() => {
      if (!data || !Array.isArray(data)) return {};

      const entityType = data.length > 0 ? data[0].entityType : "DISTRICT";
      const entityHeader =
        entityType === "SECTOR"
          ? "Sector"
          : entityType === "SCHOOL"
          ? "School"
          : "District";

      const districts = [
        ...new Set(data.map((item) => item.entityName)),
      ].sort();
      const classifications = [
        ...new Set(data.map((item) => item.classification)),
      ].sort();
      const qualifications = [
        ...new Set(data.map((item) => item.qualificationLevel)),
      ].sort();
      const teachingLevels = [
        ...new Set(data.map((item) => item.teachingLevel)),
      ].sort();

      const pivotData = {};
      const districtTotals = {};
      const grandTotals = {};
      const teachingLevelTotals = {};
      const teachingLevelGrandTotals = {};

      districts.forEach((district) => {
        pivotData[district] = {};
        districtTotals[district] = 0;
        teachingLevelTotals[district] = {};

        teachingLevels.forEach((teachingLevel) => {
          pivotData[district][teachingLevel] = {};
          teachingLevelTotals[district][teachingLevel] = 0;

          qualifications.forEach((qualification) => {
            pivotData[district][teachingLevel][qualification] = {};
            classifications.forEach((classification) => {
              pivotData[district][teachingLevel][qualification][
                classification
              ] = 0;
            });
          });
        });
      });

      teachingLevels.forEach((teachingLevel) => {
        grandTotals[teachingLevel] = {};
        teachingLevelGrandTotals[teachingLevel] = 0;

        qualifications.forEach((qualification) => {
          grandTotals[teachingLevel][qualification] = {};
          classifications.forEach((classification) => {
            grandTotals[teachingLevel][qualification][classification] = 0;
          });
        });
      });

      data.forEach((item) => {
        const district = item.entityName;
        const classification = item.classification;
        const teachingLevel = item.teachingLevel;
        const qualification = item.qualificationLevel;
        const total = item.total || 0;

        pivotData[district][teachingLevel][qualification][classification] =
          total;

        districtTotals[district] += total;

        teachingLevelTotals[district][teachingLevel] += total;

        grandTotals[teachingLevel][qualification][classification] += total;

        teachingLevelGrandTotals[teachingLevel] += total;
      });

      const overallGrandTotal = Object.values(districtTotals).reduce(
        (acc, curr) => acc + curr,
        0
      );

      const validColumns = [];
      teachingLevels.forEach((teachingLevel) => {
        qualifications.forEach((qualification) => {
          classifications.forEach((classification) => {
            const hasData = districts.some(
              (district) =>
                pivotData[district][teachingLevel][qualification][
                  classification
                ] > 0
            );
            if (hasData) {
              validColumns.push({
                teachingLevel,
                qualification,
                classification,
              });
            }
          });
        });
      });

      const columnStructure = {};
      validColumns.forEach(
        ({ teachingLevel, qualification, classification }) => {
          if (!columnStructure[teachingLevel]) {
            columnStructure[teachingLevel] = {};
          }
          if (!columnStructure[teachingLevel][qualification]) {
            columnStructure[teachingLevel][qualification] = [];
          }
          columnStructure[teachingLevel][qualification].push(classification);
        }
      );

      return {
        pivotData,
        districtTotals,
        grandTotals,
        overallGrandTotal,
        teachingLevelTotals,
        teachingLevelGrandTotals,
        districts,
        classifications,
        qualifications,
        teachingLevels,
        validColumns,
        columnStructure,
        entityHeader,
      };
    }, [data]);

    const filteredDistricts = useMemo(() => {
      if (!processedData.districts) return [];

      let filtered = processedData.districts;

      filtered.sort((a, b) => {
        if (sortBy === "total") {
          const totalA = processedData.districtTotals?.[a] || 0;
          const totalB = processedData.districtTotals?.[b] || 0;
          return totalB - totalA;
        } else {
          return a.localeCompare(b);
        }
      });

      return filtered;
    }, [processedData.districts, processedData.districtTotals, sortBy]);

    const getCellValue = (
      district,
      teachingLevel,
      qualification,
      classification
    ) => {
      const value =
        processedData.pivotData?.[district]?.[teachingLevel]?.[qualification]?.[
          classification
        ] || 0;
      return value.toLocaleString();
    };

    const getGrandTotalValue = (
      teachingLevel,
      qualification,
      classification
    ) => {
      const value =
        processedData.grandTotals?.[teachingLevel]?.[qualification]?.[
          classification
        ] || 0;
      return value.toLocaleString();
    };

    const exportToExcel = () => {
      if (!processedData.columnStructure) return;

      const wb = XLSX.utils.book_new();

      const excelData = [];

      const headerRow1 = ["SN", processedData.entityHeader || "District"];
      const headerRow2 = ["", ""];
      const headerRow3 = ["", ""];

      Object.keys(processedData.columnStructure).forEach((teachingLevel) => {
        const qualifications = processedData.columnStructure[teachingLevel];
        const totalCols =
          Object.values(qualifications).reduce(
            (acc, classifications) => acc + classifications.length,
            0
          ) + 1;

        headerRow1.push(teachingLevel.replace("_", " "));
        for (let i = 1; i < totalCols; i++) {
          headerRow1.push("");
        }

        Object.keys(qualifications).forEach((qualification) => {
          const classifications = qualifications[qualification];
          headerRow2.push(qualification);
          for (let i = 1; i < classifications.length; i++) {
            headerRow2.push("");
          }
        });
        headerRow2.push(`${teachingLevel.replace("_", " ")} Total`);

        Object.keys(qualifications).forEach((qualification) => {
          const classifications = qualifications[qualification];
          classifications.forEach((classification) => {
            headerRow3.push(classification);
          });
        });
        headerRow3.push("");
      });

      headerRow1.push("Total");
      headerRow2.push("");
      headerRow3.push("");

      excelData.push(headerRow1);
      excelData.push(headerRow2);
      excelData.push(headerRow3);

      filteredDistricts.forEach((district, index) => {
        const row = [index + 1, district];

        Object.keys(processedData.columnStructure).forEach((teachingLevel) => {
          const qualifications = processedData.columnStructure[teachingLevel];
          Object.keys(qualifications).forEach((qualification) => {
            const classifications = qualifications[qualification];
            classifications.forEach((classification) => {
              const value =
                processedData.pivotData?.[district]?.[teachingLevel]?.[
                  qualification
                ]?.[classification] || 0;
              row.push(value);
            });
          });
          row.push(
            processedData.teachingLevelTotals?.[district]?.[teachingLevel] || 0
          );
        });

        row.push(processedData.districtTotals?.[district] || 0);
        excelData.push(row);
      });

      const grandTotalRow = ["", "Grand Total"];
      Object.keys(processedData.columnStructure).forEach((teachingLevel) => {
        const qualifications = processedData.columnStructure[teachingLevel];
        Object.keys(qualifications).forEach((qualification) => {
          const classifications = qualifications[qualification];
          classifications.forEach((classification) => {
            const value =
              processedData.grandTotals?.[teachingLevel]?.[qualification]?.[
                classification
              ] || 0;
            grandTotalRow.push(value);
          });
        });
        grandTotalRow.push(
          processedData.teachingLevelGrandTotals?.[teachingLevel] || 0
        );
      });
      grandTotalRow.push(processedData.overallGrandTotal || 0);
      excelData.push(grandTotalRow);

      const ws = XLSX.utils.aoa_to_sheet(excelData);

      const colWidths = [{ wch: 5 }, { wch: 15 }];

      Object.keys(processedData.columnStructure).forEach((teachingLevel) => {
        const qualifications = processedData.columnStructure[teachingLevel];
        Object.keys(qualifications).forEach((qualification) => {
          const classifications = qualifications[qualification];
          classifications.forEach(() => {
            colWidths.push({ wch: 10 });
          });
        });
        colWidths.push({ wch: 12 });
      });
      colWidths.push({ wch: 12 });

      ws["!cols"] = colWidths;

      const merges = [];
      let colIndex = 2;

      Object.keys(processedData.columnStructure).forEach((teachingLevel) => {
        const qualifications = processedData.columnStructure[teachingLevel];
        const totalCols =
          Object.values(qualifications).reduce(
            (acc, classifications) => acc + classifications.length,
            0
          ) + 1;

        if (totalCols > 1) {
          merges.push({
            s: { r: 0, c: colIndex },
            e: { r: 0, c: colIndex + totalCols - 1 },
          });
        }

        Object.keys(qualifications).forEach((qualification) => {
          const classifications = qualifications[qualification];
          if (classifications.length > 1) {
            merges.push({
              s: { r: 1, c: colIndex },
              e: { r: 1, c: colIndex + classifications.length - 1 },
            });
          }
          colIndex += classifications.length;
        });

        colIndex += 1;
      });

      ws["!merges"] = merges;

      const headerStyle = {
        font: { bold: true },
        alignment: { horizontal: "center", vertical: "center" },
        fill: { fgColor: { rgb: "F9F9F9" } },
        border: {
          top: { style: "thin" },
          bottom: { style: "thin" },
          left: { style: "thin" },
          right: { style: "thin" },
        },
      };

      for (let row = 0; row < 3; row++) {
        for (let col = 0; col < headerRow1.length; col++) {
          const cellRef = XLSX.utils.encode_cell({ r: row, c: col });
          if (!ws[cellRef]) ws[cellRef] = { t: "s", v: "" };
          ws[cellRef].s = headerStyle;
        }
      }

      for (let row = 3; row < excelData.length; row++) {
        for (let col = 2; col < excelData[row].length; col++) {
          const cellRef = XLSX.utils.encode_cell({ r: row, c: col });
          if (ws[cellRef] && typeof ws[cellRef].v === "number") {
            ws[cellRef].z = "#,##0";
          }
        }
      }

      XLSX.utils.book_append_sheet(wb, ws, "Staff Distribution");

      const date = new Date().toISOString().split("T")[0];
      const filename = `Staff Distribution By Teaching Level, Role and Qualification As of ${date}.xlsx`;

      XLSX.writeFile(wb, filename);
    };

    if (!data || !Array.isArray(data) || data.length === 0) {
      return (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="200px"
        >
          <Typography variant="h6" color="textSecondary">
            No staff distribution data available
          </Typography>
        </Box>
      );
    }

    return (
      <Box sx={{ width: "100%" }}>
        <style>
          {`
          .hover-row:hover {
            background-color: #f0f8ff !important;
          }
          .table-container {
            overflow: auto;
          }
          .table-container table {
            font-size: 14px;
          }
          .table-container th {
            position: sticky;
            top: 0;
            z-index: 10;
            background-color: #f9f9f9 !important;
          }
        `}
        </style>
        <div className="table-container">
          <table
            style={{
              borderCollapse: "collapse",
              width: "100%",
              fontSize: "14px",
            }}
          >
            <thead>
              <tr style={{ backgroundColor: "#f9f9f9" }}>
                <th
                  rowSpan={3}
                  style={{
                    border: "1px solid #ddd",
                    padding: "12px",
                    textAlign: "center",
                    minWidth: "60px",
                    fontSize: "14px",
                    fontWeight: "bold",
                    whiteSpace: "nowrap",
                  }}
                >
                  SN
                </th>
                <th
                  rowSpan={3}
                  style={{
                    border: "1px solid #ddd",
                    padding: "12px",
                    textAlign: "center",
                    minWidth: "150px",
                    fontSize: "14px",
                    fontWeight: "bold",
                    whiteSpace: "nowrap",
                  }}
                >
                  {processedData.entityHeader || "District"}
                </th>
                {Object.keys(processedData.columnStructure || {}).map(
                  (teachingLevel) => {
                    const qualifications =
                      processedData.columnStructure[teachingLevel];
                    const colSpan =
                      Object.values(qualifications).reduce(
                        (acc, classifications) => acc + classifications.length,
                        0
                      ) + 1;
                    return (
                      <th
                        key={teachingLevel}
                        colSpan={colSpan}
                        style={{
                          border: "1px solid #ddd",
                          padding: "12px",
                          textAlign: "center",
                          fontSize: "14px",
                          fontWeight: "bold",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {teachingLevel.replace("_", " ")}
                      </th>
                    );
                  }
                )}
                <th
                  rowSpan={3}
                  style={{
                    border: "1px solid #ddd",
                    padding: "12px",
                    textAlign: "center",
                    minWidth: "90px",
                    fontSize: "14px",
                    fontWeight: "bold",
                    whiteSpace: "nowrap",
                    backgroundColor: "#fff2cc",
                  }}
                >
                  Grand Total
                </th>
              </tr>

              <tr style={{ backgroundColor: "#f9f9f9" }}>
                {Object.keys(processedData.columnStructure || {}).map(
                  (teachingLevel) => {
                    const qualifications =
                      processedData.columnStructure[teachingLevel];
                    return Object.keys(qualifications)
                      .map((qualification) => {
                        const classifications = qualifications[qualification];
                        const colSpan = classifications.length;
                        return (
                          <th
                            key={`${teachingLevel}-${qualification}`}
                            colSpan={colSpan}
                            style={{
                              border: "1px solid #ddd",
                              padding: "10px",
                              textAlign: "center",
                              fontSize: "13px",
                              fontWeight: "bold",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {qualification}
                          </th>
                        );
                      })
                      .concat([
                        <th
                          key={`${teachingLevel}-total`}
                          rowSpan={2}
                          style={{
                            border: "1px solid #ddd",
                            padding: "10px",
                            textAlign: "center",
                            fontSize: "12px",
                            fontWeight: "bold",
                            backgroundColor: "#e6f3ff",
                            whiteSpace: "nowrap",
                            minWidth: "90px",
                          }}
                        >
                          TOTAL {teachingLevel.replace("_", " ")}
                        </th>,
                      ]);
                  }
                )}
              </tr>

              <tr style={{ backgroundColor: "#f9f9f9" }}>
                {Object.keys(processedData.columnStructure || {}).map(
                  (teachingLevel) => {
                    const qualifications =
                      processedData.columnStructure[teachingLevel];
                    return Object.keys(qualifications).map((qualification) => {
                      const classifications = qualifications[qualification];
                      return classifications.map((classification) => (
                        <th
                          key={`${teachingLevel}-${qualification}-${classification}`}
                          style={{
                            border: "1px solid #ddd",
                            padding: "8px 6px",
                            textAlign: "center",
                            fontSize: "12px",
                            fontWeight: "bold",
                            minWidth: "80px",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {classification}
                        </th>
                      ));
                    });
                  }
                )}
              </tr>
            </thead>

            <tbody>
              {filteredDistricts.map((district, index) => (
                <tr
                  key={district}
                  className="hover-row"
                  style={{
                    backgroundColor: index % 2 === 0 ? "#f9f9f9" : "#fff",
                    transition: "background-color 0.2s ease",
                  }}
                >
                  <td
                    style={{
                      border: "1px solid #ddd",
                      padding: "10px",
                      textAlign: "center",
                      fontSize: "0.9rem",
                    }}
                  >
                    {index + 1}
                  </td>
                  <td
                    style={{
                      border: "1px solid #ddd",
                      padding: "10px",
                      textAlign: "left",
                      fontSize: "0.9rem",
                      fontWeight: "bold",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {district}
                  </td>
                  {Object.keys(processedData.columnStructure || {}).map(
                    (teachingLevel) => {
                      const qualifications =
                        processedData.columnStructure[teachingLevel];
                      const cells = Object.keys(qualifications)
                        .map((qualification) => {
                          const classifications = qualifications[qualification];
                          return classifications.map((classification) => (
                            <td
                              key={`${district}-${teachingLevel}-${qualification}-${classification}`}
                              style={{
                                border: "1px solid #ddd",
                                padding: "8px 4px",
                                textAlign: "center",
                                fontSize: "0.85rem",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {getCellValue(
                                district,
                                teachingLevel,
                                qualification,
                                classification
                              )}
                            </td>
                          ));
                        })
                        .flat();

                      cells.push(
                        <td
                          key={`${district}-${teachingLevel}-total`}
                          style={{
                            border: "1px solid #ddd",
                            padding: "8px 4px",
                            textAlign: "center",
                            fontSize: "0.85rem",
                            fontWeight: "bold",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {(
                            processedData.teachingLevelTotals?.[district]?.[
                              teachingLevel
                            ] || 0
                          ).toLocaleString()}
                        </td>
                      );

                      return cells;
                    }
                  )}
                  <td
                    style={{
                      border: "1px solid #ddd",
                      padding: "10px",
                      textAlign: "center",
                      fontSize: "0.9rem",
                      fontWeight: "bold",
                      whiteSpace: "nowrap",
                      backgroundColor: "#015198",
                      color: "white",
                    }}
                  >
                    {(
                      processedData.districtTotals?.[district] || 0
                    ).toLocaleString()}
                  </td>
                </tr>
              ))}

              <tr
                style={{
                  backgroundColor: "#015198",
                  fontWeight: "bold",
                  color: "white",
                }}
              >
                <td
                  style={{
                    border: "1px solid #ddd",
                    padding: "10px",
                    textAlign: "center",
                    fontSize: "0.9rem",
                    fontWeight: "bold",
                  }}
                ></td>
                <td
                  style={{
                    border: "1px solid #ddd",
                    padding: "10px",
                    textAlign: "left",
                    fontSize: "0.9rem",
                    fontWeight: "bold",
                    whiteSpace: "nowrap",
                  }}
                >
                  Grand Total
                </td>
                {Object.keys(processedData.columnStructure || {}).map(
                  (teachingLevel) => {
                    const qualifications =
                      processedData.columnStructure[teachingLevel];
                    const cells = Object.keys(qualifications)
                      .map((qualification) => {
                        const classifications = qualifications[qualification];
                        return classifications.map((classification) => (
                          <td
                            key={`total-${teachingLevel}-${qualification}-${classification}`}
                            style={{
                              border: "1px solid #ddd",
                              padding: "8px 4px",
                              textAlign: "center",
                              fontSize: "0.85rem",
                              fontWeight: "bold",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {getGrandTotalValue(
                              teachingLevel,
                              qualification,
                              classification
                            )}
                          </td>
                        ));
                      })
                      .flat();

                    cells.push(
                      <td
                        key={`total-${teachingLevel}-total`}
                        style={{
                          border: "1px solid #ddd",
                          padding: "8px 4px",
                          textAlign: "center",
                          fontSize: "0.85rem",
                          fontWeight: "bold",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {(
                          processedData.teachingLevelGrandTotals?.[
                            teachingLevel
                          ] || 0
                        ).toLocaleString()}
                      </td>
                    );

                    return cells;
                  }
                )}
                <td
                  style={{
                    border: "1px solid #ddd",
                    padding: "10px",
                    textAlign: "center",
                    fontSize: "0.9rem",
                    fontWeight: "bold",
                    whiteSpace: "nowrap",
                    backgroundColor: "rgb(237, 108, 2)",
                    color: "#fff",
                  }}
                >
                  {(processedData.overallGrandTotal || 0).toLocaleString()}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </Box>
    );
  }
);

export default StaffDistributionTable;
